//COMPONENTS
import React from 'react'
import Layout from '../layouts/default'
import {
  Get_Page_With_FilterQuery,
  Get_Page_With_FilterQueryVariables,
  Pages,
} from '@/ts/graphqlschema'
import { DOMAIN } from '@/data/metadata'
import { formatPagesMetaData } from '../utils'
import { GetStaticPaths, GetStaticProps } from 'next'
import { clientSecret } from '@/graphql/apollo-client-secret'
import { GET_PAGE_WITH_FILTER } from '@/graphql/queries/page/get_page_by_slug'
import GrapeParser from '@/components/GrapeParser'
import fetchDataForHTMLBuilder from '@/utils/fetch-html-builder-data'

type PageProps = {
  pageData: Pages
  css?: string
  component?: any
  globalData?: any
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const pageSlug = params?.slug?.toString()
  // const language = locale
  const queryParams = {
    page_filter: {
      slug: {
        _eq: pageSlug,
      },
      // language: {
      //   _eq: locale,
      // },
    },
  }
  try {
    const { data } = await clientSecret.query<Get_Page_With_FilterQuery>({
      query: GET_PAGE_WITH_FILTER,
      variables: queryParams,
      fetchPolicy: 'network-only',
    })
    const pageData = data.pages[0]

    const content = JSON.parse(pageData?.content)
    const css = content?.css
    const component = content?.projectData?.pages?.[0]?.frames?.[0]?.component

    const globalData = {}
    await fetchDataForHTMLBuilder(component, globalData)

    return {
      props: {
        pageData,
        css: css || null,
        component: component || null,
        globalData: globalData || null,
      },
      revalidate: 15,
    }
  } catch {}

  return {
    redirect: {
      destination: '/404',
      permanent: false,
    },
    revalidate: 15,
  }
}

const Page: (props: PageProps) => JSX.Element = props => {
  const { pageData, css, component, globalData } = props
  const canonicalUrl = `${DOMAIN}`

  //above & below content
  const above_content =
    pageData?.above_content && JSON.parse(pageData?.above_content?.content)
  const below_content =
    pageData?.below_content && JSON.parse(pageData?.below_content?.content)
  const above_content_css = above_content?.css
  const below_content_css = below_content?.css
  const above_content_component =
    above_content?.projectData?.pages?.[0]?.frames?.[0]?.component
  const below_content_component =
    below_content?.projectData?.pages?.[0]?.frames?.[0]?.component

  return (
    <Layout
      shouldRenderHighlight
      shouldRenderContacts
      metadata={formatPagesMetaData({ ...pageData, canonicalUrl })}
    >
      {/*Above Content*/}
      <div>
        {above_content_component && (
          <GrapeParser component={above_content_component} globalData={{}} />
        )}
        {above_content_css && <style jsx>{above_content_css}</style>}
      </div>

      <div>
        {component && (
          <GrapeParser
            component={component}
            currentItem={pageData}
            globalData={globalData}
          />
        )}
        {css && <style jsx>{css}</style>}
      </div>

      {/*Below Content*/}
      <div>
        {below_content_component && (
          <GrapeParser component={below_content_component} globalData={{}} />
        )}
        {below_content_css && <style jsx>{below_content_css}</style>}
      </div>
    </Layout>
  )
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export const getStaticPaths: GetStaticPaths = async () => {
  const params = {
    page_filter: {
      status: {
        _eq: 'published',
      },
      page_type: {
        _eq: 'static-page',
      },
    },
    sort: '-publish_date',
  }
  const { data, error } = await clientSecret.query<
    Get_Page_With_FilterQuery,
    Get_Page_With_FilterQueryVariables
  >({
    query: GET_PAGE_WITH_FILTER,
    variables: params,
    fetchPolicy: 'network-only',
  })
  const paths =
  error || !data?.pages
    ? []
    : data?.pages
        ?.filter(page => page?.slug !== null)
        .map(page => ({
          params: { slug: page?.slug },
        }))
  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' }
}

export default Page
